import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TABS = [
  {
    key: 'urlsToCrawl',
    label: 'URLs to Crawl',
    description: (
      <>
        <strong>Description: </strong>These are base URLs of various websites that you would like to crawl for new potential donation pages.
      </>
    ),
    columns: (
      <>
        Upload a CSV file with the following column:
        <br />
        <code className="bg-gray-100 px-2 py-1 rounded">url</code> - The URL to crawl (will be stripped to base URL)
      </>
    ),
  },
  {
    key: 'urlsToProcess',
    label: 'URLs to Process',
    description: (
      <>
        <strong>Description: </strong>Provide full URLs that you already know contain donation data, which you would like processed automatically.
      </>
    ),
    columns: (
      <>
        Upload a CSV file with the following column:
        <br />
        <code className="bg-gray-100 px-2 py-1 rounded">full_url</code> - The complete URL to process
      </>
    ),
  },
  {
    key: 'highProfileDonors',
    label: 'High Profile Donors',
    description: (
      <>
        <strong>Description: </strong>These are known donors that we should search for news articles on. Name is the only required field, and the rest will be used at a later date, potentially.
      </>
    ),
    columns: (
      <>
        Upload a CSV file with the following columns:
        <br />
        <code className="bg-gray-100 px-2 py-1 rounded">name</code> - Donor name
        <br />
        <code className="bg-gray-100 px-2 py-1 rounded">state</code> - State
        <br />
        <code className="bg-gray-100 px-2 py-1 rounded">affiliations</code> - Affiliations
        <br />
        <code className="bg-gray-100 px-2 py-1 rounded">notes</code> - Additional notes
      </>
    ),
  },
];

const DataImportPage = () => {
  const [files, setFiles] = useState({
    urlsToCrawl: null,
    urlsToProcess: null,
    highProfileDonors: null
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState(TABS[0].key);

  const handleFileChange = (e, type) => {
    setFiles({
      ...files,
      [type]: e.target.files[0]
    });
  };

  const handleUpload = async (type) => {
    if (!files[type]) {
      setMessage(`Please select a file for ${type} first`);
      return;
    }

    const formData = new FormData();
    formData.append('file', files[type]);

    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post(`${API_URL}/api/import/${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setMessage(response.data.message);
      setFiles({ ...files, [type]: null });
    } catch (error) {
      setMessage(error.response?.data?.message || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = (tab) => (
    <div className="p-6 border rounded-lg mt-4">
      <h2 className="text-xl font-semibold mb-4">{tab.label}</h2>
      <p className="mb-4 text-gray-600">{tab.description}</p>
      <p className="mb-4 text-gray-600">{tab.columns}</p>
      <div className="flex items-center gap-4">
        <input
          type="file"
          accept=".csv"
          onChange={(e) => handleFileChange(e, tab.key)}
          className="border p-2 rounded"
        />
        <button
          onClick={() => handleUpload(tab.key)}
          disabled={loading || !files[tab.key]}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
        >
          Upload
        </button>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Data Import</h1>

      {message && (
        <div className="mb-4 p-4 rounded bg-blue-100 text-blue-700">
          {message}
        </div>
      )}
      <p className="mb-6 text-gray-600">
        Use this page to import data for crawling and processing. Ensure your CSV files are formatted correctly as described below. 
        Include headers in all CSV files, to match the expected format. 
      </p>

      {/* Tab Navigation */}
      <div className="flex border-b mb-2">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 -mb-px font-semibold border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-blue-500'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTabContent(TABS.find(tab => tab.key === activeTab))}
    </div>
  );
};

export default DataImportPage;